import {
  GearIcon,
  MaskOffIcon,
  MixerVerticalIcon,
  ResetIcon,
} from '@radix-ui/react-icons';
import {
  SCREEN_CONFIG_ROUTE,
  SCREEN_EDITOR_ROUTE,
  SCREEN_CONTROL_ROUTE,
  SCREEN_MODERATION_ROUTE,
  SCREENS_ROUTE,
} from '@/routes/consts';
import { ShieldIcon } from '@/components/ShieldIcon';
import { IconComponent } from '@/types/common';
import { useBansEnabled } from '@/hooks/useBansEnabled';
import { useAuth } from '@/hooks/useAuth';
import { Role } from '@/generated/graphql';
import {
  SectionalContainer,
  SectionDefinition,
} from '../../../components/SectionalContainer';
import { Link, useParams } from 'react-router-dom';
import { generatePath } from 'react-router-dom';
import { FullScreenLoader } from '@/components/Loader';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { LiveSwitch } from '../event/LiveSwitch/LiveSwitch';
import { ReturnFromStripeDialog } from '../event/ReturnFromStripeDialog/ReturnFromStripeDialog';
import { AdminScreenThemeStateProvider } from '@/providers/AdminScreenThemeStateProvider/AdminScreenThemeStateProvider';
import { ScreenInfoProvider } from '@/providers/ScreenInfoProvider/ScreenInfoProvider';
import { useAdminEvent } from '@/hooks/useAdminEvent';
import { Footer } from '@/components/Footer';

type ScreenAdminNavItem = SectionDefinition & {
  roles: Role[];
  id:
    | 'settings'
    | 'editor'
    | 'control'
    | 'moderation'
    | 'engagements'
    | 'insights'
    | 'back';
};

const NAV_ITEMS: ScreenAdminNavItem[] = [
  {
    id: 'settings',
    title: 'General',
    href: SCREEN_CONFIG_ROUTE,
    Icon: GearIcon,
    roles: [Role.Admin, Role.User],
  },
  {
    id: 'editor',
    title: 'Editor',
    href: SCREEN_EDITOR_ROUTE,
    Icon: MaskOffIcon,
    roles: [Role.Admin, Role.User],
  },
  // {
  //   id: 'engagements',
  //   title: 'Engagements',
  //   href: SCREEN_ENGAGEMENTS_ROUTE,
  //   Icon: SectionIcon,
  //   roles: [Role.Admin, Role.User],
  // },
  {
    id: 'control',
    title: 'Control',
    href: SCREEN_CONTROL_ROUTE,
    Icon: MixerVerticalIcon,
    roles: [Role.Admin, Role.User],
  },
  {
    id: 'moderation',
    title: 'Moderation',
    href: SCREEN_MODERATION_ROUTE,
    Icon: ShieldIcon as IconComponent,
    roles: [Role.Admin, Role.User],
  },
  // {
  //   id: 'insights',
  //   title: 'Insights',
  //   href: SCREEN_INSIGHTS_ROUTE,
  //   Icon: BarChartIcon,
  //   roles: [Role.Admin, Role.User],
  // },
] as const;

export function ScreenAdminContainer({
  section,
  children,
}: {
  children: React.ReactNode;
  section: ScreenAdminNavItem['id'];
}) {
  const { user } = useAuth();
  const { id } = useParams();
  const { bansEnabled } = useBansEnabled();
  const { data, loading, error } = useAdminEvent({ id: id || '' });
  const sections = NAV_ITEMS.filter(
    (item) =>
      user?.role &&
      item.roles.includes(user?.role) &&
      (item.id !== 'moderation' || bansEnabled),
  ).map((item) => ({
    ...item,
    href: generatePath(item.href, { id }),
  }));

  const event = data?.event;

  if (loading) {
    return <FullScreenLoader />;
  }

  if (error || !event) {
    return <p>Error: {error?.message || 'Event not found'}</p>;
  }

  return (
    <ScreenInfoProvider event={event}>
      <AdminScreenThemeStateProvider event={event}>
        <SectionalContainer
          name="SCREEN_ADMIN"
          currentSection={section}
          sections={sections}
          navStartSlot={
            <div className="flex items-center gap-2">
              <Link
                to={generatePath(SCREENS_ROUTE, { id })}
                className={cn(
                  buttonVariants({ variant: 'ghost', size: 'sm' }),
                  'inline-flex items-center gap-2',
                )}
              >
                <ResetIcon />
                <span>back</span>
              </Link>
              <h4 className="flex items-center gap-2 whitespace-nowrap p-2 text-lg md:p-0">
                {/* <span className="text-muted-foreground">Screen:</span> */}
                <span>{event.name}</span>
              </h4>
            </div>
          }
          navEndSlot={<LiveSwitch />}
        >
          {children}
        </SectionalContainer>
        <Footer />
        <ReturnFromStripeDialog />
      </AdminScreenThemeStateProvider>
    </ScreenInfoProvider>
  );
}
